<!--
  - Copyright (c) 2025. Haodong JU
  -->

<template>
  <v-dialog
    v-model="show"
    max-width="900px"
  >
    <v-card>
      <div class="headline d-flex pa-4">
        <div class="d-flex align-center">
          <v-avatar
            v-if="asset && asset.image"
            class="mr-3"
            size="40"
          >
            <v-img
              :src="asset.image"
              alt="Asset Image"
            />
          </v-avatar>
          <div>
            {{ asset ? asset.name : '资产详情' }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          icon
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-divider />

      <v-tabs
        v-model="activeTab"
        background-color="transparent"
        color="primary"
      >
        <v-tab>详细信息</v-tab>
        <v-tab>分发记录</v-tab>
        <v-tab>兑换码</v-tab>
        <v-tab>操作日志</v-tab>
      </v-tabs>

      <div>
        <v-tabs-items v-model="activeTab">
          <!-- 详细信息 Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <div
                  v-if="asset"
                  class="asset-details"
                >
                  <!-- Asset Metrics -->
                  <div class="asset-metrics mb-4">
                    <div class="metric-card">
                      <div class="metric-icon">
                        <v-icon color="primary">
                          mdi-package-variant
                        </v-icon>
                      </div>
                      <div class="metric-content">
                        <div class="metric-value">
                          {{ asset.currentStock }}
                        </div>
                        <div class="metric-label">
                          当前库存
                        </div>
                      </div>
                    </div>

                    <div class="metric-card">
                      <div class="metric-icon">
                        <v-icon color="info">
                          mdi-share-variant
                        </v-icon>
                      </div>
                      <div class="metric-content">
                        <div class="metric-value">
                          {{ asset.distributed }}
                        </div>
                        <div class="metric-label">
                          已分发
                        </div>
                      </div>
                    </div>

                    <div class="metric-card">
                      <div class="metric-icon">
                        <v-icon color="success">
                          mdi-check-circle
                        </v-icon>
                      </div>
                      <div class="metric-content">
                        <div class="metric-value">
                          {{ asset.used }}
                        </div>
                        <div class="metric-label">
                          已使用
                        </div>
                      </div>
                    </div>

                    <div class="metric-card">
                      <div class="metric-icon">
                        <v-icon color="error">
                          mdi-clock-end
                        </v-icon>
                      </div>
                      <div class="metric-content">
                        <div class="metric-value">
                          {{ asset.expired }}
                        </div>
                        <div class="metric-label">
                          已过期
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Asset Details Grid -->
                  <div class="asset-details-grid">
                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon
                          small
                          class="mr-1"
                        >
                          mdi-power
                        </v-icon>
                        激活状态
                      </div>
                      <div class="detail-value">
                        <v-chip
                          :color="asset.active ? 'success' : 'error'"
                          dark
                          small
                        >
                          {{ asset.active ? '激活' : '未激活' }}
                        </v-chip>
                      </div>
                    </div>

                    <div
                      class="detail-item"
                      v-if="asset.unlimitedSupply"
                    >
                      <div class="detail-label">
                        <v-icon
                          small
                          class="mr-1"
                        >
                          mdi-infinity
                        </v-icon>
                        供应类型
                      </div>
                      <div class="detail-value">
                        <v-chip
                          color="primary"
                          dark
                          small
                        >
                          无限供应
                        </v-chip>
                      </div>
                    </div>

                    <div
                      class="detail-item"
                      v-if="asset.supportsRedemptionCode"
                    >
                      <div class="detail-label">
                        <v-icon
                          small
                          class="mr-1"
                        >
                          mdi-ticket-confirmation
                        </v-icon>
                        兑换码支持
                      </div>
                      <div class="detail-value">
                        <v-chip
                          color="info"
                          dark
                          small
                        >
                          支持兑换码
                        </v-chip>
                      </div>
                    </div>

                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon
                          small
                          class="mr-1"
                        >
                          mdi-shape
                        </v-icon>
                        类型
                      </div>
                      <div class="detail-value">
                        {{ asset.assetType }}
                      </div>
                    </div>

                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon
                          small
                          class="mr-1"
                        >
                          mdi-package-variant-closed
                        </v-icon>
                        初始库存
                      </div>
                      <div class="detail-value">
                        {{ asset.initialStock }}
                      </div>
                    </div>

                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon
                          small
                          class="mr-1"
                        >
                          mdi-calendar-start
                        </v-icon>
                        生效时间
                      </div>
                      <div class="detail-value">
                        {{ asset.activeSinceDisplay }}
                      </div>
                    </div>

                    <div class="detail-item">
                      <div class="detail-label">
                        <v-icon
                          small
                          class="mr-1"
                        >
                          mdi-calendar-end
                        </v-icon>
                        截止时间
                      </div>
                      <div class="detail-value">
                        {{ asset.expiredAtDisplay }}
                      </div>
                    </div>

                    <div
                      class="detail-item"
                      v-if="asset.claimDaysLimit"
                    >
                      <div class="detail-label">
                        <v-icon
                          small
                          class="mr-1"
                        >
                          mdi-timer-sand
                        </v-icon>
                        领取期限
                      </div>
                      <div class="detail-value">
                        {{ asset.claimDaysLimit }} 天
                      </div>
                    </div>

                    <div
                      class="detail-item"
                      v-if="asset.supportsRedemptionCode && asset.redemptionCodePrefix"
                    >
                      <div class="detail-label">
                        <v-icon
                          small
                          class="mr-1"
                        >
                          mdi-ticket-confirmation
                        </v-icon>
                        兑换码前缀
                      </div>
                      <div class="detail-value">
                        {{ asset.redemptionCodePrefix }}
                      </div>
                    </div>
                  </div>

                  <div class="detail-item full-width">
                    <div class="detail-label">
                      <v-icon
                        small
                        class="mr-1"
                      >
                        mdi-text-box
                      </v-icon>
                      描述
                    </div>
                    <div class="detail-value">
                      {{ asset.description || '无' }}
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="text-center pa-4"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- 分发记录 Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <div class="d-flex align-center mb-4">
                  <v-text-field
                    v-model="distributionRecordSearch"
                    label="搜索分发记录"
                    prepend-icon="mdi-magnify"
                    clearable
                    hide-details
                    dense
                    outlined
                    class="mr-2"
                  />
                  <v-select
                    v-model="distributionStatusFilter"
                    :items="distributionStatusOptions"
                    label="状态筛选"
                    clearable
                    hide-details
                    dense
                    outlined
                    class="mr-2"
                    style="max-width: 150px"
                  />
                  <v-spacer />
                  <v-btn
                    color="primary"
                    small
                    @click="refreshDistributionRecords"
                    class="mr-2"
                  >
                    <v-icon left>
                      mdi-refresh
                    </v-icon>
                    刷新
                  </v-btn>
                </div>

                <!-- 分发记录统计信息 -->
                <div class="distribution-summary mb-4 pa-3">
                  <div class="d-flex align-center">
                    <v-icon
                      color="primary"
                      class="mr-2"
                    >
                      mdi-information-outline
                    </v-icon>
                    <span class="text-body-1">共有 <strong>{{ totalDistributionRecords }}</strong> 条记录，其中 <strong>{{ pendingDistributionRecords }}</strong> 条待使用</span>
                  </div>
                </div>

                <div
                  v-if="loadingDistributionRecords"
                  class="text-center pa-4"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                  />
                </div>

                <div
                  v-else-if="filteredDistributionRecords.length === 0"
                  class="text-center pa-4"
                >
                  <v-icon
                    large
                    color="grey lighten-1"
                  >
                    mdi-share-variant-outline
                  </v-icon>
                  <div class="mt-2 grey--text">
                    暂无分发记录数据
                  </div>
                </div>

                <!-- 显示分发记录列表 -->
                <div
                  v-else
                  class="distribution-records-container"
                >
                  <div class="record-cards">
                    <v-lazy
                      v-for="(record, index) in filteredDistributionRecords"
                      :key="index"
                      min-height="100"
                    >
                      <div class="record-card-wrapper">
                        <div class="record-card">
                          <!-- 卡片内容 -->
                          <div class="record-card-content">
                          <!-- 分发者信息和状态信息 -->
                          <div class="record-header">
                            <div class="record-user">
                              <span
                                class="record-user-name"
                                v-if="record.distributedBy"
                              >{{ record.distributedBy }}</span>
                              <span
                                v-else
                                class="record-user-name"
                              >未知分发者</span>
                            </div>
                            <div class="record-status">
                              <v-chip
                                :color="getStatusColor(record.status)"
                                dark
                                x-small
                                class="status-chip"
                              >
                                {{ getActionDisplayName(record.status) || record.status }}
                              </v-chip>
                            </div>
                          </div>

                          <!-- 时间信息 - 根据状态显示不同的时间 -->
                          <div class="record-time">
                            <!-- 已分发状态只显示分发时间 -->
                            <div
                              v-if="record.status === 'DISTRIBUTED'"
                              class="record-time-item"
                            >
                              <v-icon
                                small
                                color="primary"
                                class="mr-1"
                              >
                                mdi-calendar
                              </v-icon>
                              <span>{{ record.distributedAtDisplay }}</span>
                            </div>

                            <!-- 已使用状态只显示使用时间 -->
                            <div
                              v-else-if="record.status === 'USED'"
                              class="record-time-item used"
                            >
                              <v-icon
                                small
                                color="success"
                                class="mr-1"
                              >
                                mdi-calendar-check
                              </v-icon>
                              <span>{{ record.usedAtDisplay }}</span>
                            </div>

                            <!-- 已过期状态显示过期时间 -->
                            <div
                              v-else-if="record.status === 'EXPIRED'"
                              class="record-time-item expired"
                            >
                              <v-icon
                                small
                                color="error"
                                class="mr-1"
                              >
                                mdi-clock-end
                              </v-icon>
                              <span>{{ record.expiredAtDisplay }}</span>
                            </div>

                            <!-- 其他状态显示分发时间 -->
                            <div
                              v-else
                              class="record-time-item"
                            >
                              <v-icon
                                small
                                color="grey"
                                class="mr-1"
                              >
                                mdi-calendar
                              </v-icon>
                              <span>{{ record.distributedAtDisplay }}</span>
                            </div>
                          </div>

                          <!-- 底部信息栏 -->
                          <div class="record-footer">
                            <div class="record-meta">
                              <span class="record-user-id text-caption">
                                <v-icon x-small>mdi-account</v-icon>
                                {{ record.userDisplayName || record.userId }}
                                <span
                                  v-if="record.userEmail"
                                  class="ml-1"
                                >{{ record.userEmail }}</span>
                              </span>
                              <span
                                v-if="record.distributionNote || record.note"
                                class="record-note"
                              >
                                <v-icon x-small>mdi-note-text</v-icon>
                                {{ record.distributionNote || record.note }}
                              </span>
                            </div>
                            <v-btn
                              v-if="record.status === 'DISTRIBUTED'"
                              x-small
                              text
                              color="primary"
                              class="record-action-btn"
                              @click.stop="removeRecord(record)"
                            >
                              <v-icon
                                x-small
                                left
                              >
                                mdi-reload
                              </v-icon>
                              撤销
                            </v-btn>
                          </div>
                        </div>
                      </div>
                      </div>
                    </v-lazy>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- 兑换码 Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <div class="d-flex align-center mb-4">
                  <v-text-field
                    v-model="redemptionCodeSearch"
                    label="搜索兑换码"
                    prepend-icon="mdi-magnify"
                    clearable
                    hide-details
                    dense
                    outlined
                    class="mr-2"
                  />
                  <v-select
                    v-model="redemptionCodeStatusFilter"
                    :items="redemptionStatusOptions"
                    label="状态筛选"
                    clearable
                    hide-details
                    dense
                    outlined
                    class="mr-2"
                    style="max-width: 150px"
                  />
                  <v-spacer />
                  <v-btn
                    color="primary"
                    small
                    @click="refreshRedemptionCodes"
                    class="mr-2"
                  >
                    <v-icon left>
                      mdi-refresh
                    </v-icon>
                    刷新
                  </v-btn>
                </div>

                <div
                  v-if="loadingRedemptionCodes"
                  class="text-center pa-4"
                >
                  <v-progress-circular
                    indeterminate
                    color="primary"
                  />
                </div>

                <div
                  v-else-if="filteredRedemptionCodes.length === 0"
                  class="text-center pa-4"
                >
                  <v-icon
                    large
                    color="grey lighten-1"
                  >
                    mdi-ticket-confirmation-outline
                  </v-icon>
                  <div class="mt-2 grey--text">
                    暂无兑换码数据
                  </div>
                </div>

                <div
                  v-else
                  class="redemption-codes-container"
                >
                  <v-list>
                    <v-list-item
                      v-for="(code, index) in filteredRedemptionCodes"
                      :key="index"
                      class="mb-2 redemption-code-item"
                    >
                      <!-- Status on the far left -->
                      <v-chip
                        :color="getRedemptionStatusColor(code.status)"
                        dark
                        x-small
                        class="mr-3"
                      >
                        {{ code.status }}
                      </v-chip>

                      <!-- Used/Max usage count -->
                      <div class="usage-count mr-3">
                        <v-icon x-small class="mr-1">mdi-counter</v-icon>
                        <span class="text-body-2">{{ code.usageCount }}/{{ code.maxUsageCount }}</span>
                      </div>

                      <!-- Code -->
                      <div class="code-container mr-3">
                        <span class="text-subtitle-2 code-text">{{ code.code }}</span>
                      </div>

                      <!-- Note (if available) - shown as tooltip -->
                      <v-tooltip
                        v-if="code.note"
                        bottom
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            x-small
                            class="mr-3"
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-note-text
                          </v-icon>
                        </template>
                        <span>{{ code.note }}</span>
                      </v-tooltip>

                      <v-spacer></v-spacer>

                      <!-- Creation time moved to the right -->
                      <div class="creation-time mr-3">
                        <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                        <span class="text-body-2">{{ code.createAtDisplay }}</span>
                      </div>

                      <!-- Action button on the far right -->
                      <v-btn
                        x-small
                        text
                        color="primary"
                        @click.stop="removeRedemptionCode(code)"
                        :disabled="code.status !== 'ACTIVE'"
                      >
                        <v-icon x-small left>mdi-reload</v-icon>
                        撤销
                      </v-btn>
                    </v-list-item>
                  </v-list>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <!-- 操作日志 Tab -->
          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <div
                  v-if="!assetLogs || assetLogs.length === 0"
                  class="text-center pa-4"
                >
                  <v-icon
                    large
                    color="grey lighten-1"
                  >
                    mdi-clipboard-text-outline
                  </v-icon>
                  <div class="mt-2 grey--text">
                    暂无操作日志数据
                  </div>
                </div>

                <div
                  v-else
                  class="asset-logs-container"
                >
                  <v-timeline dense>
                    <v-timeline-item
                      v-for="(log, index) in assetLogs"
                      :key="index"
                      :color="getLogActionColor(log.action)"
                      small
                    >
                      <template #opposite>
                        <span class="text-caption">{{ formatLogTime(log.actionTime) }}</span>
                      </template>
                      <div class="log-item-content">
                        <div class="d-flex align-center">
                          <v-chip
                            x-small
                            :color="getLogActionColor(log.action)"
                            dark
                            class="mr-2"
                          >
                            {{ getActionDisplayName(log.action) }}
                          </v-chip>
                          <span class="text-subtitle-2">{{ log.actionBy || '系统' }}</span>
                        </div>

                        <div class="log-details mt-2">
                          <div
                            v-if="log.userId"
                            class="log-detail-item"
                          >
                            <v-icon
                              x-small
                              class="mr-1"
                            >
                              mdi-account
                            </v-icon>
                            <span class="text-caption">用户ID: </span>
                            <span class="text-body-2">{{ log.userId }}</span>
                          </div>

                          <div
                            v-if="log.actionSource"
                            class="log-detail-item"
                          >
                            <v-icon
                              x-small
                              class="mr-1"
                            >
                              mdi-source-branch
                            </v-icon>
                            <span class="text-caption">来源: </span>
                            <span class="text-body-2">{{ log.actionSource }}</span>
                          </div>

                          <div
                            v-if="log.note"
                            class="log-detail-item"
                          >
                            <v-icon
                              x-small
                              class="mr-1"
                            >
                              mdi-note-text
                            </v-icon>
                            <span class="text-caption">备注: </span>
                            <span class="text-body-2">{{ log.note }}</span>
                          </div>

                          <div
                            v-if="log.details"
                            class="log-detail-item"
                          >
                            <v-icon
                              x-small
                              class="mr-1"
                            >
                              mdi-information-outline
                            </v-icon>
                            <span class="text-caption">详情: </span>
                            <span class="text-body-2">{{ log.details }}</span>
                          </div>
                        </div>
                      </div>
                    </v-timeline-item>
                  </v-timeline>
                </div>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </div>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          color="primary"
          text
          @click="editAsset"
        >
          <v-icon left>
            mdi-pencil
          </v-icon>
          编辑资产
        </v-btn>
        <v-btn
          color="primary"
          text
          @click="distributeAsset"
        >
          <v-icon left>
            mdi-send
          </v-icon>
          分发资产
        </v-btn>
        <v-btn
          color="primary"
          text
          @click="createRedemptionCode"
        >
          <v-icon left>
            mdi-code-array
          </v-icon>
          创建兑换码
        </v-btn>
        <v-btn
          color="primary"
          text
          @click="adjustInventory"
        >
          <v-icon left>
            mdi-storefront-outline
          </v-icon>
          调整库存
        </v-btn>
        <v-spacer />
        <v-btn
          color="error"
          text
          @click="deleteAsset"
        >
          <v-icon left>
            mdi-delete
          </v-icon>
          删除
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AssetRecord, { cancelRecord } from '@/model/dataLayer/api/MembershipSystem/AssetRecord'
import AssetRedemption, { cancelRedemptionCode } from '@/model/dataLayer/api/MembershipSystem/AssetRedemption'
import { showErrorMessage, showSuccessMessage } from '@/model/utils/utils'
import { useBaseFormDialogStore } from '@/model/baseForm/baseFormDialogStore'
import IKUtils from 'innerken-js-utils'
import Asset, {
  adjustInventory as adjustAssetInventory,
  getAssetDetail
} from '@/model/dataLayer/api/MembershipSystem/Asset'
import dayjs from 'dayjs'

export default {
  name: 'AssetDetailDialog',
  components: {
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    assetId: {
      type: [String, Number],
      default: null
    }
  },
  data () {
    return {
      activeTab: 0,
      asset: null,
      assetRecordModel: AssetRecord,
      assetRedemptionModel: AssetRedemption,
      recordDetail: null,
      redemptionCode: null,
      loading: false,

      // New data structure for asset detail
      assetLogs: [],

      // Redemption code tab data
      redemptionCodes: [],
      loadingRedemptionCodes: false,
      redemptionCodeSearch: '',
      redemptionCodeStatusFilter: null,
      redemptionStatusOptions: [
        { text: '激活', value: 'ACTIVE' },
        { text: '已兑换', value: 'REDEEMED' },
        { text: '已过期', value: 'EXPIRED' },
        { text: '已撤销', value: 'REVOKED' }
      ],
      redemptionCodeHeaders: [
        { text: '兑换码', value: 'code', sortable: true },
        { text: '状态', value: 'status', sortable: true },
        { text: '最大使用次数', value: 'maxUsageCount', sortable: true },
        { text: '已使用次数', value: 'usageCount', sortable: true },
        { text: '创建时间', value: 'createAtDisplay', sortable: true },
        { text: '备注', value: 'note', sortable: false },
        { text: '操作', value: 'actions', sortable: false, align: 'center' }
      ],

      // Distribution records tab data
      distributionRecords: [],
      loadingDistributionRecords: false,
      distributionRecordSearch: '',
      distributionStatusFilter: null,
      distributionStatusOptions: [
        { text: '已分发', value: 'DISTRIBUTED' },
        { text: '已使用', value: 'USED' },
        { text: '已过期', value: 'EXPIRED' },
        { text: '已撤销', value: 'REVOKED' }
      ],
      distributionRecordHeaders: [
        { text: '用户', value: 'userId', sortable: true },
        { text: '状态', value: 'status', sortable: true },
        { text: '分发人', value: 'distributedBy', sortable: true },
        { text: '分发时间', value: 'distributedAtDisplay', sortable: true },
        { text: '使用时间', value: 'usedAtDisplay', sortable: true },
        { text: '备注', value: 'note', sortable: false },
        { text: '操作', value: 'actions', sortable: false, align: 'center' }
      ]
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    filteredRedemptionCodes () {
      if (!this.redemptionCodes) return []

      return this.redemptionCodes.filter(code => {
        // Filter by search text
        const searchMatch = !this.redemptionCodeSearch ||
          code.code.toLowerCase().includes(this.redemptionCodeSearch.toLowerCase()) ||
          (code.note && code.note.toLowerCase().includes(this.redemptionCodeSearch.toLowerCase()))

        // Filter by status
        const statusMatch = !this.redemptionCodeStatusFilter || code.status === this.redemptionCodeStatusFilter

        return searchMatch && statusMatch
      })
    },
    filteredDistributionRecords () {
      if (!this.distributionRecords) return []

      return this.distributionRecords.filter(record => {
        // Filter by search text
        const searchMatch = !this.distributionRecordSearch ||
          (record.userId && record.userId.toString().includes(this.distributionRecordSearch)) ||
          (record.userDisplayName && record.userDisplayName.toLowerCase().includes(this.distributionRecordSearch.toLowerCase())) ||
          (record.userEmail && record.userEmail.toLowerCase().includes(this.distributionRecordSearch.toLowerCase())) ||
          (record.distributedBy && record.distributedBy.toLowerCase().includes(this.distributionRecordSearch.toLowerCase())) ||
          (record.distributionNote && record.distributionNote.toLowerCase().includes(this.distributionRecordSearch.toLowerCase())) ||
          (record.note && record.note.toLowerCase().includes(this.distributionRecordSearch.toLowerCase()))

        // Filter by status
        const statusMatch = !this.distributionStatusFilter || record.status === this.distributionStatusFilter

        return searchMatch && statusMatch
      })
    },
    totalDistributionRecords () {
      return this.distributionRecords ? this.distributionRecords.length : 0
    },
    pendingDistributionRecords () {
      if (!this.distributionRecords) return 0
      return this.distributionRecords.filter(record => record.status === 'DISTRIBUTED').length
    },
    revokeRecordSchemas () {
      return [
        {
          key: 'revokedBy',
          name: 'revokedBy',
          hint: '撤销操作执行人',
          required: true,
          componentProps: {
            placeholder: '请输入撤销操作执行人姓名'
          }
        },
        {
          key: 'reason',
          name: 'reason',
          hint: '撤销原因',
          required: true,
          componentProps: {
            placeholder: '请输入撤销原因',
            rules: [v => !!v || '撤销原因不能为空', v => v.length <= 200 || '撤销原因不能超过200个字符']
          }
        }
      ]
    },
    revokeRedemptionSchemas () {
      return [
        {
          key: 'reason',
          name: 'reason',
          hint: '撤销兑换码的原因',
          required: true,
          componentProps: {
            placeholder: '请输入撤销兑换码的原因',
            rules: [v => !!v || '撤销原因不能为空', v => v.length <= 200 || '撤销原因不能超过200个字符']
          }
        }
      ]
    },
    inventorySchemas () {
      return [
        {
          key: 'desiredInventory',
          name: '实际库存',
          hint: `(原库存为:${this.asset?.currentStock ?? 0})`,
          required: true,
          componentProps: {
            type: 'number',
            rules: [v => v !== '' || '库存不能为空', v => !isNaN(Number(v)) || '请输入有效数字']
          }
        }
      ]
    },
    redemptionCodeSchemas () {
      return [
        {
          key: 'single',
          name: '单个/批量',
          hint: '单个或批量创建兑换码',
          component: 'v-select',
          default: true,
          tag: 'basic',
          componentProps: {
            items: [
              { text: '单个', value: true },
              { text: '批量', value: false }
            ]
          }
        },
        {
          key: 'code',
          name: 'code',
          hint: '如果单独创建,在这里输入指定兑换码,批量生成则不用填写',
          default: '',
          required: true,
          tag: 'basic',
          componentProps: {
            placeholder: '请输入兑换码',
            rules: [v => !v || /^[A-Za-z0-9-_]+$/.test(v) || '兑换码只能包含字母、数字、连字符和下划线']
          },
          preconditions: { single: true }
        },
        {
          key: 'count',
          name: 'count',
          hint: '如果批量生成,在这里输入指定数量,请不要填写Code',
          default: '',
          required: true,
          tag: 'basic',
          componentProps: {
            type: 'number',
            placeholder: '请输入生成数量',
            rules: [
              v => !v || !isNaN(Number(v)) || '请输入有效数字',
              v => !v || Number(v) > 0 || '数量必须大于0',
              v => !v || Number(v) <= 1000 || '单次最多生成1000个'
            ]
          },
          preconditions: { single: false }
        },
        {
          key: 'maxUsageCount',
          name: 'maxUsageCount',
          hint: '最大使用次数',
          default: 1,
          required: true,
          tag: 'settings',
          componentProps: {
            type: 'number',
            placeholder: '请输入最大使用次数',
            rules: [
              v => !!v || '最大使用次数不能为空',
              v => Number(v) > 0 || '最大使用次数必须大于0'
            ]
          }
        },
        {
          key: 'expiresAt',
          name: 'expiresAt',
          component: 'date-selector',
          hint: '过期时间',
          required: false,
          tag: 'settings'
        },
        {
          key: 'note',
          name: 'note',
          hint: '备注信息',
          required: false,
          tag: 'settings',
          default: '',
          componentProps: {
            placeholder: '请输入备注信息'
          }
        }
      ]
    }
  },
  watch: {
    assetId: {
      immediate: true,
      async handler (newVal) {
        if (newVal) {
          await this.loadAsset()
        }
      }
    },
    show (newVal) {
      if (newVal && this.assetId) {
        this.loadAsset()
      }
    }
  },
  methods: {
    getStatusColor (status) {
      if (status === 'DISTRIBUTED') return 'info'
      if (status === 'USED') return 'green'
      if (status === 'EXPIRED') return 'red'
      if (status === 'REVOKED') return 'grey'
      return ''
    },
    getStatusColorClass (status) {
      if (status === 'DISTRIBUTED') return 'status-distributed'
      if (status === 'USED') return 'status-used'
      if (status === 'EXPIRED') return 'status-expired'
      if (status === 'REVOKED') return 'status-revoked'
      return ''
    },
    getRedemptionStatusColor (status) {
      if (status === 'REDEEMED') return 'info'
      if (status === 'ACTIVE') return 'green'
      if (status === 'EXPIRED') return 'red'
      if (status === 'REVOKED') return 'grey'
      return ''
    },

    getLogActionColor (action) {
      switch (action) {
        case 'CREATE':
          return 'green'
        case 'UPDATE':
          return 'blue'
        case 'DISTRIBUTE':
          return 'purple'
        case 'USE':
          return 'deep-purple'
        case 'EXPIRE':
          return 'orange'
        case 'REVOKE':
          return 'red'
        case 'REDEEM':
          return 'teal'
        default:
          return 'grey'
      }
    },

    formatLogTime (time) {
      return time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },

    getActionDisplayName (action) {
      const actionMap = {
        // Action types
        CREATE: '创建',
        UPDATE: '更新',
        DISTRIBUTE: '分发',
        USE: '使用',
        EXPIRE: '过期',
        REVOKE: '撤销',
        REDEEM: '兑换',

        // Status types
        DISTRIBUTED: '已分发',
        USED: '已使用',
        EXPIRED: '已过期',
        REVOKED: '已撤销'
      }
      return actionMap[action] || action
    },
    async loadAsset () {
      try {
        this.loading = true
        this.loadingRedemptionCodes = true
        this.loadingDistributionRecords = true

        // Get all asset details in a single API call
        const assetDetail = await getAssetDetail(this.assetId)

        // Set asset data
        this.$set(this, 'asset', assetDetail.asset)

        // Set redemption codes
        this.$set(this, 'redemptionCodes', assetDetail.redemptionCodes || [])

        // Set distribution records
        this.$set(this, 'distributionRecords', assetDetail.records || [])

        // Store logs
        this.$set(this, 'assetLogs', assetDetail.logs || [])
      } catch (error) {
        console.error('Error loading asset details:', error)
        showErrorMessage('加载资产详情失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
        this.loadingRedemptionCodes = false
        this.loadingDistributionRecords = false
      }
    },

    // These methods are kept for backward compatibility but now just refresh all data
    async loadRedemptionCodes () {
      await this.loadAsset()
    },

    async loadDistributionRecords () {
      await this.loadAsset()
    },

    async refreshRedemptionCodes () {
      await this.loadRedemptionCodes()
    },

    async refreshDistributionRecords () {
      await this.loadDistributionRecords()
    },

    close () {
      this.show = false
      this.asset = null
      this.activeTab = 0
      this.assetLogs = []
      this.redemptionCodes = []
      this.distributionRecords = []
    },
    async editAsset () {
      this.$emit('edit', this.asset)
      // Don't close the dialog when editing asset
    },
    async distributeAsset () {
      this.$emit('distribute', this.asset)
      // Don't close the dialog when distributing asset
    },
    async createRedemptionCode () {
      this.$emit('create-redemption', this.asset)
      // Don't close the dialog when creating redemption code
    },
    async adjustInventory () {
      if (this.asset.unlimitedSupply) {
        showErrorMessage('无限供应的资产不需要调整库存')
        return
      }

      const store = useBaseFormDialogStore()

      // Define the schema for the inventory adjustment dialog
      const schema = {
        title: '调整库存',
        subtitle: `调整 ${this.asset.name || '资产'} 的库存`,
        schemas: this.inventorySchemas
      }

      // Open the dialog and wait for the result
      const result = await store.editItem(schema)

      // Handle the result
      if (result) {
        try {
          await adjustAssetInventory(this.asset.id, result)
          showSuccessMessage('库存调整成功！')
          await this.loadAsset()
        } catch (error) {
          console.error('Error adjusting inventory:', error)
          showErrorMessage('调整库存失败: ' + (error.message || '未知错误'))
        }
      }
    },
    async deleteAsset () {
      if (confirm(`确定要删除资产 "${this.asset.name}" 吗？此操作不可撤销。`)) {
        try {
          await Asset.remove(this.asset.id)
          showSuccessMessage('资产删除成功')
          this.$emit('deleted')
          this.close()
        } catch (error) {
          console.error('Error deleting asset:', error)
          showErrorMessage('删除资产失败: ' + (error.message || '未知错误'))
        }
      }
    },
    async removeRecord (item) {
      try {
        // Deep copy the item to avoid modifying the original
        this.recordDetail = IKUtils.deepCopy(item)

        const store = useBaseFormDialogStore()

        // Define the schema for the revoke dialog
        const schema = {
          title: '撤销记录',
          subtitle: '请输入撤销原因',
          schemas: this.revokeRecordSchemas
        }

        // Open the dialog and wait for the result
        const result = await store.editItem(schema)

        // Handle the result
        if (result) {
          await cancelRecord(this.recordDetail.id, result)
          showSuccessMessage('撤销成功！')
          await this.loadDistributionRecords()
          await this.loadAsset() // Reload asset to update counts
        }
      } catch (error) {
        console.error('Error revoking record:', error)
        showErrorMessage('撤销记录失败: ' + (error.message || '未知错误'))
      }
    },
    async removeRedemptionCode (item) {
      try {
        // Deep copy the item to avoid modifying the original
        this.redemptionCode = IKUtils.deepCopy(item)

        const store = useBaseFormDialogStore()

        // Define the schema for the revoke dialog
        const schema = {
          title: '撤销兑换码',
          subtitle: '请输入撤销原因',
          schemas: this.revokeRedemptionSchemas
        }

        // Open the dialog and wait for the result
        const result = await store.editItem(schema)

        // Handle the result
        if (result) {
          await cancelRedemptionCode(this.redemptionCode.code, result)
          showSuccessMessage('兑换码撤销成功！')
          await this.loadRedemptionCodes()
        }
      } catch (error) {
        console.error('Error revoking redemption code:', error)
        showErrorMessage('撤销兑换码失败: ' + (error.message || '未知错误'))
      }
    }
  }
}
</script>

<style scoped>
.v-card__title {
  word-break: break-word;
}

.asset-details {
  padding: 8px;
}

.asset-details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px 16px;
  transition: background-color 0.2s;
}

.detail-item:hover {
  background-color: #e8e8e8;
}

.detail-label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 1rem;
  font-weight: 500;
}

.full-width {
  grid-column: 1 / -1;
  margin-top: 16px;
}

/* Media query for responsive layout */
@media (max-width: 600px) {
  .asset-details-grid {
    grid-template-columns: 1fr;
  }

  .asset-metrics {
    grid-template-columns: 1fr 1fr;
  }
}

/* Distribution Records Styles */
.distribution-records-container {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
}

/* Record Cards Styles */
.record-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.record-card-wrapper {
  margin-bottom: 0;
}

.record-card {
  position: relative;
  display: flex;
  border-radius: 8px;
  background-color:#f5f5f5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
}

.record-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Distribution summary styles */
.distribution-summary {
  background-color: #f5f7fa;
  border-radius: 8px;
  border-left: 4px solid #2196F3;
}

.record-card-content {
  flex: 1;
  padding: 12px 16px;
}

.record-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
}

.record-user {
  display: flex;
  flex-direction: column;
}

.record-user-name {
  font-weight: 500;
  font-size: 0.95rem;
}

.record-user-email {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.record-time {
  margin-top: 8px;
  margin-bottom: 8px;
}

.record-time-item {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  margin-bottom: 4px;
}

.record-time-item.used {
  color: #4CAF50; /* success color */
}

.record-time-item.expired {
  color: #FF5252; /* error color */
}

.record-status {
  display: flex;
  align-items: center;
}

.status-chip {
  font-size: 0.7rem;
  height: 20px !important;
}

.record-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.record-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.record-distributor, .record-note {
  display: flex;
  align-items: center;
  gap: 4px;
}

.record-note {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.record-action-btn {
  margin-left: auto;
}

/* User Records Section Styles */
.user-records-section {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.user-info-header {
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.user-records-grid {
  margin-top: 12px;
}

.record-card {
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
}

.record-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

.record-details {
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.record-detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 0;
  flex: 1 1 auto;
  min-width: 150px;
}

/* User List Styles */
.user-list-item {
  border-radius: 8px;
  margin-bottom: 8px;
  transition: background-color 0.2s, transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.user-list-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
}

.user-list-item .v-list-item__title {
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .user-records-grid {
    grid-template-columns: 1fr;
  }

  .user-info-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .user-info-header .v-avatar {
    margin-bottom: 8px;
  }
}

/* Redemption Codes Styles */
.redemption-codes-container {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
}

/* Redemption codes list styles */
.redemption-codes-container .v-list-item {
  border-radius: 8px;
  background-color: #f5f5f5;
  margin-bottom: 8px;
  transition: background-color 0.2s, transform 0.2s, box-shadow 0.2s;
}

.redemption-codes-container .v-list-item:hover {
  background-color: #e8e8e8;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05) !important;
}

.redemption-code-item {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  min-height: 48px;
}

/* Override Vuetify's default v-list-item styles */
.redemption-code-item.v-list-item {
  min-height: unset;
}

.redemption-code-item.v-list-item .v-list-item__content {
  padding: 0;
}

.code-container {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.code-text {
  font-family: monospace;
  font-weight: 500;
}

.usage-count, .creation-time {
  display: flex;
  align-items: center;
  white-space: nowrap;
}

/* Keep these for backward compatibility */
.code-details {
  margin-top: 8px;
}

.code-detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

/* Asset Logs Styles */
.asset-logs-container {
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
}

.log-item-content {
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.log-details {
  margin-top: 8px;
}

.log-detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

/* Asset Details Styles */

.color-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.asset-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.metric-card {
  display: flex;
  align-items: center;
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: rgba(0, 0, 0, 0.03);
  margin-right: 12px;
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.2;
}

.metric-label {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

/* Media queries removed as we're now using a single column layout by default */
</style>
